import React, { useEffect } from 'react';
import { X, Download, Share2 } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

interface ChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  chartData: any;
  chartOptions: ChartOptions<'line'>;
  onExport: () => void;
  onShare: () => void;
  darkMode: boolean;
  isExporting: boolean;
}

const ChartModal: React.FC<ChartModalProps> = ({ isOpen, onClose, chartData, chartOptions, onExport, onShare, darkMode, isExporting }) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) onClose(); };
    if (isOpen) { document.addEventListener('keydown', handleEscape); document.body.style.overflow = 'hidden'; }
    return () => { document.removeEventListener('keydown', handleEscape); document.body.style.overflow = 'unset'; };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalChartOptions: ChartOptions<'line'> = {
    ...chartOptions,
    plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins?.title, font: { size: 20, weight: 'bold' } }, legend: { ...chartOptions.plugins?.legend, labels: { ...chartOptions.plugins?.legend?.labels, padding: 30, font: { size: 14 } } } }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-appines-black/75 transition-opacity" onClick={onClose} />
      <div className={`relative w-full max-w-6xl max-h-[90vh] rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-appines-petrol' : 'bg-appines-white'}`}>
        <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]'}`}>
          <h2 className={`text-xl font-bold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>Evolution des Revenus - Vue Détaillée</h2>
          <div className="flex items-center space-x-2">
            <button onClick={onExport} disabled={isExporting}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${isExporting ? 'opacity-50 cursor-not-allowed' : 'bg-appines-emerald hover:bg-[#22b886] text-appines-petrol'}`}>
              <Download className={`h-4 w-4 mr-2 ${isExporting ? 'animate-pulse' : ''}`} />{isExporting ? 'Export...' : 'Télécharger PNG'}
            </button>
            <button onClick={onShare} className="flex items-center px-4 py-2 bg-appines-emerald hover:bg-[#22b886] text-appines-petrol rounded-lg transition-colors">
              <Share2 className="h-4 w-4 mr-2" />Partager
            </button>
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10' : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'}`}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="relative h-96 md:h-[500px]"><Line data={chartData} options={modalChartOptions} /></div>
          <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-appines-white/5' : 'bg-[rgba(1,42,54,0.04)]'}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className={`text-sm ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>Zone géographique</p>
                <p className={`font-semibold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>Métropole / DROM</p>
              </div>
              <div className="text-center">
                <p className={`text-sm ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>Période d'affichage</p>
                <p className={`font-semibold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>Sélectionnée</p>
              </div>
              <div className="text-center">
                <p className={`text-sm ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>Nombre d'actes</p>
                <p className={`font-semibold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>Dans le panier</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartModal;
